import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, Filter, Clock, CreditCard, TrendingUp } from "lucide-react";

const CHART_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

function getToday() { return new Date().toISOString().slice(0, 10); }
function getYesterday() { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }

function buildParams(datePreset, dateFrom, dateTo, diningFilter) {
  const params = new URLSearchParams();
  if (datePreset === "today") {
    params.append("date_from", getToday());
    params.append("date_to", getToday() + "T23:59:59");
  } else if (datePreset === "yesterday") {
    params.append("date_from", getYesterday());
    params.append("date_to", getYesterday() + "T23:59:59");
  } else if (datePreset === "custom") {
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo + "T23:59:59");
  } else {
    params.append("days", datePreset);
  }
  if (diningFilter !== "all") params.append("dining_type_id", diningFilter);
  return params.toString();
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState("sales");
  const [salesReport, setSalesReport] = useState(null);
  const [hourlyReport, setHourlyReport] = useState(null);
  const [paymentReport, setPaymentReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState("7");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [diningTypes, setDiningTypes] = useState([]);
  const [diningFilter, setDiningFilter] = useState("all");

  useEffect(() => {
    api.get("/dining-types").then((r) => setDiningTypes(r.data.filter((d) => d.is_active))).catch(() => {});
  }, []);

  useEffect(() => {
    const qs = buildParams(datePreset, dateFrom, dateTo, diningFilter);
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [sRes, hRes, pRes] = await Promise.all([
          api.get(`/reports/sales?${qs}`),
          api.get(`/reports/hourly?${qs}`),
          api.get(`/reports/payments?${qs}`),
        ]);
        setSalesReport(sRes.data);
        setHourlyReport(hRes.data);
        setPaymentReport(pRes.data);
      } catch (err) {
        console.error("Failed to load reports:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [datePreset, dateFrom, dateTo, diningFilter]);

  const handleDatePreset = (val) => {
    setDatePreset(val);
    if (val !== "custom") { setDateFrom(""); setDateTo(""); }
  };

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            {salesReport?.total_orders || 0} orders | ${(salesReport?.total_revenue || 0).toFixed(2)} revenue
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5 space-y-3" data-testid="reports-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="h-4 w-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Period:</span>
          {[
            { id: "today", label: "Today" },
            { id: "yesterday", label: "Yesterday" },
            { id: "7", label: "Last 7 Days" },
            { id: "14", label: "Last 14 Days" },
            { id: "30", label: "Last 30 Days" },
            { id: "90", label: "Last 90 Days" },
            { id: "custom", label: "Custom Range" },
          ].map((p) => (
            <Button key={p.id} variant={datePreset === p.id ? "default" : "outline"} size="sm"
              className={`text-xs h-8 ${datePreset === p.id ? "bg-[var(--brand-primary)] text-white" : ""}`}
              onClick={() => handleDatePreset(p.id)} data-testid={`report-period-${p.id}`}
            >{p.label}</Button>
          ))}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="report-date-from" />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid="report-date-to" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Order Type:</span>
          <Button variant={diningFilter === "all" ? "default" : "outline"} size="sm"
            className={`text-xs h-8 ${diningFilter === "all" ? "bg-[var(--brand-primary)] text-white" : ""}`}
            onClick={() => setDiningFilter("all")} data-testid="report-dining-all"
          >All Types</Button>
          {diningTypes.map((dt) => (
            <Button key={dt.dining_type_id} variant={diningFilter === dt.dining_type_id ? "default" : "outline"} size="sm"
              className={`text-xs h-8 ${diningFilter === dt.dining_type_id ? "bg-[var(--brand-primary)] text-white" : ""}`}
              onClick={() => setDiningFilter(dt.dining_type_id)} data-testid={`report-dining-${dt.dining_type_id}`}
            >{dt.short_name} - {dt.name}</Button>
          ))}
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-5" data-testid="report-tabs">
          <TabsTrigger value="sales" data-testid="tab-sales" className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> Sales Summary
          </TabsTrigger>
          <TabsTrigger value="hourly" data-testid="tab-hourly" className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Hourly Sales
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> Payment Summary
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>
        ) : (
          <>
            {/* === SALES SUMMARY TAB === */}
            <TabsContent value="sales">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="daily-sales-chart">
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Daily Revenue</h3>
                  {salesReport?.daily_sales?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={salesReport.daily_sales}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} />
                        <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <p className="text-sm text-slate-400 text-center py-16">No sales data for this period</p>}
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="payment-methods-chart">
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Payment Methods</h3>
                  {salesReport?.payment_methods?.length > 0 ? (
                    <div className="flex items-center gap-8">
                      <ResponsiveContainer width="50%" height={200}>
                        <PieChart>
                          <Pie data={salesReport.payment_methods} dataKey="total" nameKey="method" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                            {salesReport.payment_methods.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-3">
                        {salesReport.payment_methods.map((pm, i) => (
                          <div key={pm.method} className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                            <span className="text-sm capitalize text-slate-600">{pm.method}</span>
                            <span className="text-sm font-medium text-slate-900 ml-auto">${pm.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <p className="text-sm text-slate-400 text-center py-16">No payment data</p>}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="top-products-section">
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Top Products</h3>
                {salesReport?.top_products?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={salesReport.top_products.slice(0, 5)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} />
                        <Bar dataKey="revenue" fill="#2563EB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="text-xs">Product</TableHead>
                        <TableHead className="text-xs text-right">Qty</TableHead>
                        <TableHead className="text-xs text-right">Revenue</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {salesReport.top_products.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm font-medium">{p.name}</TableCell>
                            <TableCell className="text-sm text-right">{p.quantity}</TableCell>
                            <TableCell className="text-sm text-right font-medium">${p.revenue.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : <p className="text-sm text-slate-400 text-center py-8">No product data</p>}
              </div>
            </TabsContent>

            {/* === HOURLY SALES TAB === */}
            <TabsContent value="hourly">
              <div className="bg-white border border-slate-200 rounded-lg" data-testid="hourly-sales-section">
                <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-slate-900">Hourly Sales</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Sales performance broken down by hour of day</p>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Total Sales</p>
                      <p className="font-bold text-slate-900">${(hourlyReport?.totals?.sales || 0).toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Transactions</p>
                      <p className="font-bold text-slate-900">{hourlyReport?.totals?.transactions || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Hourly Bar Chart */}
                {hourlyReport?.hourly && (
                  <div className="px-6 py-4 border-b border-slate-100">
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={hourlyReport.hourly.filter((h) => h.sales > 0 || h.transactions > 0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={(v) => { const p = v < 12 ? "a" : "p"; return `${v % 12 || 12}${p}`; }} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v, name) => [name === "sales" ? `$${v.toFixed(2)}` : v, name === "sales" ? "Sales" : "Orders"]} labelFormatter={(v) => { const p = v < 12 ? "AM" : "PM"; return `${v % 12 || 12}:00 ${p}`; }} />
                        <Bar dataKey="sales" fill="#2563EB" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Hourly Table */}
                <div className="overflow-x-auto" data-testid="hourly-sales-table">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold">Time Slot</TableHead>
                        <TableHead className="text-xs font-semibold text-right"># Transactions</TableHead>
                        <TableHead className="text-xs font-semibold text-right"># Items</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Avg. Sales/Check</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Sales</TableHead>
                        <TableHead className="text-xs font-semibold text-right">% Sales</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(hourlyReport?.hourly || []).map((row) => {
                        const isActive = row.transactions > 0;
                        const isHigh = row.pct_sales >= 15;
                        return (
                          <TableRow key={row.hour} className={isActive ? "" : "text-slate-300"} data-testid={`hourly-row-${row.hour}`}>
                            <TableCell className={`text-sm ${isActive ? "font-medium text-slate-700" : "text-slate-400"}`}>{row.label}</TableCell>
                            <TableCell className="text-sm text-right">{row.transactions}</TableCell>
                            <TableCell className="text-sm text-right">{row.items}</TableCell>
                            <TableCell className="text-sm text-right">{isActive ? `$${row.avg_check.toFixed(2)}` : "-"}</TableCell>
                            <TableCell className="text-sm text-right font-medium">${row.sales.toFixed(2)}</TableCell>
                            <TableCell className={`text-sm text-right font-bold ${isHigh ? "text-[var(--brand-primary)]" : isActive ? "text-slate-600" : "text-slate-300"}`}>{row.pct_sales}%</TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      {hourlyReport?.totals && (
                        <TableRow className="bg-slate-50 border-t-2 border-slate-300" data-testid="hourly-totals-row">
                          <TableCell className="text-sm font-bold text-slate-900">Totals:</TableCell>
                          <TableCell className="text-sm text-right font-bold text-slate-900">{hourlyReport.totals.transactions}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-slate-900">{hourlyReport.totals.items}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-slate-900">${hourlyReport.totals.avg_check.toFixed(2)}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-slate-900">${hourlyReport.totals.sales.toFixed(2)}</TableCell>
                          <TableCell className="text-sm text-right font-bold text-[var(--brand-primary)]">100%</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* === PAYMENT SUMMARY TAB === */}
            <TabsContent value="payments">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Payment Breakdown */}
                <div className="bg-white border border-slate-200 rounded-lg" data-testid="payment-summary-section">
                  <div className="border-b border-slate-200 px-6 py-4">
                    <h2 className="font-heading text-lg font-semibold text-slate-900">Payment Summary</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Breakdown by payment method</p>
                  </div>
                  <div className="overflow-x-auto" data-testid="payment-summary-table">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs font-semibold">Payment Method</TableHead>
                          <TableHead className="text-xs font-semibold text-right"># Orders</TableHead>
                          <TableHead className="text-xs font-semibold text-right">Amount</TableHead>
                          <TableHead className="text-xs font-semibold text-right">% of Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(paymentReport?.methods || []).map((m, i) => (
                          <TableRow key={m.method} data-testid={`payment-row-${m.method}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                                <span className="text-sm font-medium text-slate-900 capitalize">{m.method}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-right">{m.count}</TableCell>
                            <TableCell className="text-sm text-right font-medium">${m.total.toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-right font-bold text-[var(--brand-primary)]">{m.pct.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                        {paymentReport && (
                          <TableRow className="bg-slate-50 border-t-2 border-slate-300" data-testid="payment-totals-row">
                            <TableCell className="text-sm font-bold text-slate-900">Total Tender Payments</TableCell>
                            <TableCell className="text-sm text-right font-bold text-slate-900">{paymentReport.total_transactions}</TableCell>
                            <TableCell className="text-sm text-right font-bold text-slate-900">${paymentReport.grand_total.toFixed(2)}</TableCell>
                            <TableCell className="text-sm text-right font-bold text-[var(--brand-primary)]">100%</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Payment Chart + Stats */}
                <div className="space-y-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="payment-chart">
                    <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">Payment Distribution</h3>
                    {paymentReport?.methods?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={paymentReport.methods} dataKey="total" nameKey="method" cx="50%" cy="50%" innerRadius={50} outerRadius={85} label={({ method, pct }) => `${method} ${pct}%`}>
                            {paymentReport.methods.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-sm text-slate-400 text-center py-12">No payment data</p>}
                  </div>

                  {/* Payment Stats Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    {(paymentReport?.methods || []).map((m, i) => (
                      <div key={m.method} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`payment-stat-${m.method}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="text-xs font-semibold text-slate-500 uppercase capitalize">{m.method}</span>
                        </div>
                        <p className="text-xl font-bold text-slate-900">${m.total.toFixed(2)}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-slate-400">{m.count} orders</span>
                          <span className="text-xs text-slate-400">Avg ${m.avg_check.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
